import type { GridRowId } from '@mui/x-data-grid-pro';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

import TextEditorForTableBlock from './TextEditorForTableBlock.tsx';
import type { RootState } from '../../../grid/reduxStore/Store';
import { useAppSelector } from '../../../grid/reduxStore/Store';
import { selectContentTableRows } from '../../../grid/reduxStore/editorSlice';
import { selectAllVariablesItems } from '../../../grid/reduxStore/allVariableSlice';
import type { ColumnTypeIdentifier } from '../../../grid/reduxStore/table.types';
import { useMemo } from 'react';
import { isJson } from '../../../../../utils/isJson.ts';
import { useSelector } from 'react-redux';
import { TableSettingsTypes } from '../../../SidePanel/content/TableSettings/types.ts';
import {
  DEFAULT_CURRENCY_CODE,
  selectActiveCurrencyCode,
  selectContentType,
} from 'components/editor/grid/reduxStore/documentSettingsSlice';
import useFeatureFlag from '../../../../../hooks/useFeatureFlag.ts';
import { FeatureFlags } from '../../../../../utils/featureFlags.ts';
import { ContentType } from 'utils/getContentType';
import { isGeneratedRow } from '../../../grid/reduxStore/crmRowMarkers';

interface CustomTableCellProps {
  id: GridRowId;
  hasFocus?: boolean;
  field: string;
  columnType?: ColumnTypeIdentifier;
  rowType?: string;
  blockId?: string;
  sectionId?: string;
  numberOfDecimalPlaces?: number;
}

export const TipTapRenderCell = ({ columnType, rowType, id, field, blockId, sectionId }: CustomTableCellProps) => {
  const { t } = useTranslation();
  const rowData = useAppSelector((state: RootState) => selectContentTableRows(state, sectionId as string, blockId as string))?.find(
    (row) => row.id === id
  );
  const variables = useAppSelector(selectAllVariablesItems);
  const isCurrencySettingsEnabled = useFeatureFlag([FeatureFlags.currencySettings])[FeatureFlags.currencySettings];
  const activeCurrencyCode = useAppSelector(selectActiveCurrencyCode) || DEFAULT_CURRENCY_CODE;
  const documentCurrencyCode = isCurrencySettingsEnabled ? activeCurrencyCode : DEFAULT_CURRENCY_CODE;
  const contentType = useAppSelector(selectContentType);
  const { activeSidePanel } = useSelector((state: RootState) => state.blockStyleSettings);
  const isActiveRowsPanel = activeSidePanel === TableSettingsTypes.TABLE_ROWS || activeSidePanel === TableSettingsTypes.TABLE_FOOTER_ROWS;
  const valueDisplay = rowData?.[field];
  const documentContent = useMemo(() => (isJson(valueDisplay) ? JSON.parse(valueDisplay) : valueDisplay), [valueDisplay]);

  if (!id) {
    return <></>;
  }

  const fallbackTooltipTitle = rowData?.crmGroupUnrelatedFallback ? t('document.grid.table.crm_group_unrelated_fallback_tooltip') : '';

  return (
    <Tooltip title={fallbackTooltipTitle} placement="bottom">
      <div className={`editable-cell-wrapper editable-cell-${id}`} data-column-type={columnType} data-row-type={rowType}>
        <TextEditorForTableBlock
          editable={!isGeneratedRow(rowData)}
          value={documentContent}
          field={field}
          id={id as GridRowId}
          variables={variables}
          columnType={columnType}
          isActiveRowsPanel={isActiveRowsPanel}
          documentCurrencyCode={documentCurrencyCode}
          disableSuggestions={contentType === ContentType.SECTION}
        />
      </div>
    </Tooltip>
  );
};
